import { Competency_Map_Status, CompetencyMap, useUpdateCompetencyMapStatusMutation } from "@generated/graphql";
import typography from "constants/typography.constants";
import { ConfirmModalContext } from "context/confirmModalContext";
import { ToastContext, ToastTypeEnum } from "context/toastContext";
import { parseApolloError } from "helpers/errorsParser";
import { useContext } from "react";

interface UseArchiveCompetencyMapParams {
  competencyMap?: {
    id: CompetencyMap["id"];
    trialsInfo: Pick<CompetencyMap["trialsInfo"], "hasStarted">;
  } | null;
  onArchiveCompetencyMap?: () => void;
}

const useUpdateCompetencyMapStatus = ({ competencyMap, onArchiveCompetencyMap }: UseArchiveCompetencyMapParams) => {
  const { addToast } = useContext(ToastContext);
  const { handleOpen: handleOpenConfirmModal } = useContext(ConfirmModalContext);
  const [updateMapStatus] = useUpdateCompetencyMapStatusMutation();

  const handleUpdateCompetencyMapStatus = async (status: Competency_Map_Status) => {
    if (!competencyMap) return;

    await updateMapStatus({
      variables: {
        input: {
          id: competencyMap.id,
          status,
        },
      },
    })
      .then(() => {
        addToast({ type: ToastTypeEnum.SUCCESS, text: typography.competencyMap.statusChanged });
        if (onArchiveCompetencyMap) onArchiveCompetencyMap();
      })
      .catch((e) => parseApolloError(e, { addToast }));
  };

  const handleArchiveCompetencyMap = () => {
    if (!competencyMap) return;

    if (competencyMap.trialsInfo.hasStarted) {
      addToast({
        type: ToastTypeEnum.WARNING,
        text: typography.competencyMap.archive.disabled,
      });
    } else {
      handleOpenConfirmModal({
        title: typography.competencyMap.archive.title,
        description: typography.competencyMap.archive.description,
        actionButtonText: typography.competencyMap.archive.confirm,
        handleClick: () => handleUpdateCompetencyMapStatus(Competency_Map_Status.Archive),
      });
    }
  };

  return { handleArchiveCompetencyMap, handleUpdateCompetencyMapStatus };
};

export default useUpdateCompetencyMapStatus;
